import styles from './OrderStatGrid.module.css';

export interface OrderStat {
    label: string;
    /** A count, or preformatted text such as "12/100" */
    value: number | string;
    /** Whether the stat counts as 0 for the all-zero (grey) look. Defaults to `value === 0`, so set it for text values. */
    isZero?: boolean;
}

interface OrderStatGridProps {
    stats: OrderStat[];
    isLoading?: boolean;
    /** Smaller tiles, for when something else (e.g. a banner) shares the fixed-height card with the grid */
    compact?: boolean;
}

type TileLayout = 'default' | 'compact' | 'inline';

// On small screens every stat is one compact row (label left, value right), stacked. From md up: up to 3
// stats sit in one row of fixed-height tiles, label above value — or, when `compact`, shorter tiles sized
// to their content. 4 switch to a 2x2 grid whose rows share the remaining height instead, with each
// tile's label and value inline — so the grid still fits the fixed-height card.
const isTwoByTwo = (count: number) => count === 4;

/** The small-screen row look every layout starts from; the layouts below only change it from md up */
const TILE_BASE_CLASS_NAME = 'flex items-center justify-between gap-2 rounded-xl px-4 py-3';

const TILE_LAYOUT_CLASS_NAMES: Record<TileLayout, string> = {
    default: 'md:h-35 md:flex-col md:items-stretch md:p-4',
    compact: 'md:flex-col md:items-stretch md:justify-start md:gap-1 md:p-3',
    inline: 'md:h-full md:py-0',
};

const OrderStatTile: React.FC<OrderStat & { isLoading?: boolean; layout: TileLayout; isEmpty: boolean }> = ({
    label,
    value,
    isLoading,
    layout,
    isEmpty,
}) => (
    <div
        className={`${TILE_BASE_CLASS_NAME} ${TILE_LAYOUT_CLASS_NAMES[layout]} ${isEmpty ? styles.tileEmpty : styles.tile}`}
    >
        <div className="text-body-md font-semibold">{label}</div>
        <div className={`font-bold text-heading-lg ${layout === 'default' ? 'md:text-heading-xl' : ''}`}>
            {isLoading ? '–' : value}
        </div>
    </div>
);

export const OrderStatGrid: React.FC<OrderStatGridProps> = ({ stats, isLoading, compact }) => {
    const twoByTwo = isTwoByTwo(stats.length);
    const layout: TileLayout = twoByTwo ? 'inline' : compact ? 'compact' : 'default';
    // Also true while loading (every value is still 0), so the tiles start out neutral
    const isEmpty = stats.every((stat) => stat.isZero ?? stat.value === 0);

    return (
        <div
            className={
                twoByTwo
                    ? 'grid grid-cols-1 gap-3 md:min-h-0 md:flex-1 md:grid-cols-2 md:grid-rows-2'
                    : 'grid grid-cols-1 gap-3 md:grid-cols-3'
            }
        >
            {stats.map(({ label, value }) => (
                <OrderStatTile
                    key={label}
                    label={label}
                    value={value}
                    isLoading={isLoading}
                    layout={layout}
                    isEmpty={isEmpty}
                />
            ))}
        </div>
    );
};
